import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Paper,
  TextField,
  InputAdornment,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowBack, 
  Search, 
  Delete, 
  Favorite, 
  FavoriteBorder,
  Clear
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Conversation } from '@/models/conversation';
import { useAppStore } from '@/store';
import { useInfiniteSearchConversationsQuery } from '@/features/conversations/hooks/useConversationsQuery';
import {
  useUpdateConversationMutation,
  useBulkDeleteConversationsMutation,
} from '@/features/conversations/hooks/useConversationMutations';
import { queryKeys } from '@/lib/queryKeys';
import {
  QueryEmptyState,
  QueryErrorState,
  QueryLoadingState,
  QueryStaleDataWarning,
} from '@/components/state';

// Bossard Blue colors
const BOSSARD_BLUE = '#007ac3';
const BOSSARD_BLUE_10 = 'rgba(0, 122, 195, 0.1)';

// Group conversations by date
const groupConversationsByDate = (conversations: (Conversation & { preview?: string })[]) => {
  const groups: { [key: string]: (Conversation & { preview?: string })[] } = {};
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);
  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 30);

  conversations.forEach(conv => {
    const convDate = new Date(conv.updated_date);
    const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());
    
    let groupKey: string;
    
    if (convDateOnly.getTime() === today.getTime()) {
      groupKey = 'Today';
    } else if (convDateOnly.getTime() === yesterday.getTime()) {
      groupKey = 'Yesterday';
    } else if (convDateOnly >= last7Days) {
      groupKey = 'Previous 7 Days';
    } else if (convDateOnly >= last30Days) {
      groupKey = 'Previous 30 Days';
    } else {
      // Group by year for older conversations
      groupKey = convDate.getFullYear().toString();
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(conv);
  });

  // Sort groups in chronological order
  const orderedKeys = ['Today', 'Yesterday', 'Previous 7 Days', 'Previous 30 Days'];
  const years = Object.keys(groups)
    .filter(key => !orderedKeys.includes(key))
    .sort((a, b) => parseInt(b) - parseInt(a));
  
  return { groups, orderedKeys: [...orderedKeys.filter(k => groups[k]), ...years] };
};

// Format date for display
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const convDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  if (convDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  return date.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' });
};

const ChatHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedConversation, setConversationList } = useAppStore();
  
  // Mutation hooks
  const updateConversationMutation = useUpdateConversationMutation();
  const bulkDeleteConversationsMutation = useBulkDeleteConversationsMutation();
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  // committedSearch drives the actual API query — only updated on Enter or Search button click
  const [committedSearch, setCommittedSearch] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  
  // Data state
  const [error, setError] = useState<string | null>(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const LIMIT = 20;

  const triggerSearch = () => setCommittedSearch(searchTerm);
  
  // Helper to format date as YYYY-MM-DD without timezone conversion
  const formatDateForApi = (date: Date, endOfDay: boolean = false): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return endOfDay 
      ? `${year}-${month}-${day}T23:59:59.999Z`
      : `${year}-${month}-${day}T00:00:00.000Z`;
  };
  
  const {
    data: conversationPages,
    isPending,
    isFetchingNextPage,
    isError,
    isRefetchError,
    error: queryError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteSearchConversationsQuery(
    {
      search: committedSearch || undefined,
      from_date: fromDate ? formatDateForApi(fromDate, false) : undefined,
      to_date: toDate ? formatDateForApi(toDate, true) : undefined,
      favorites_only: favoritesOnly,
    },
    LIMIT
  );

  const conversations = useMemo(
    () => conversationPages?.pages.flatMap(page => page.conversations) ?? [],
    [conversationPages]
  );

  const total = conversationPages?.pages[0]?.total ?? 0;
  const hasMore = hasNextPage ?? false;
  const loading = isPending || isFetchingNextPage;
  const queryErrorMessage = isError
    ? (queryError instanceof Error ? queryError.message : 'Failed to load conversations')
    : null;
  const hasCachedConversations = conversations.length > 0;
  const showStaleDataWarning = Boolean(
    isRefetchError && queryErrorMessage && hasCachedConversations
  );
  const blockingQueryError = hasCachedConversations ? null : queryErrorMessage;
  
  // Initial load and filter changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [committedSearch, fromDate, toDate, favoritesOnly]);
  
  // Handle conversation click
  const handleConversationClick = (conv: Conversation) => {
    setSelectedConversation(conv);
    navigate('/');
  };

  // Handle favorite toggle
  const handleToggleFavorite = async (e: React.MouseEvent, conv: Conversation) => {
    e.stopPropagation();

    try {
      await updateConversationMutation.mutateAsync({
        conversationId: conv.id,
        updates: { is_favourite: !conv.is_favourite },
      });
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  // Handle selection
  const handleSelectConversation = (e: React.ChangeEvent<HTMLInputElement>, convId: string) => {
    e.stopPropagation();
    
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(convId)) {
        newSet.delete(convId);
      } else {
        newSet.add(convId);
      }
      return newSet;
    });
  };
  
  const handleSelectAll = () => {
    if (selectedIds.size === conversations.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map(c => c.id)));
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    setDeleting(true);
    
    // Capture before clearing state
    const deletedIds = new Set(selectedIds);

    try {

      const result = await bulkDeleteConversationsMutation.mutateAsync({
        conversationIds: Array.from(selectedIds),
      });

      
      setSelectedIds(new Set());
      setDeleteDialogOpen(false);
      

      if (result.errors.length > 0) {
        setError(`Deleted ${result.deleted_count} conversations. ${result.errors.length} failed.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversations');
    } finally {
      setDeleting(false);
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setCommittedSearch('');
    setFromDate(null);
    setToDate(null);
    setFavoritesOnly(false);
  };
  

  
  const hasActiveFilters = committedSearch || searchTerm || fromDate || toDate || favoritesOnly;
  const displayError = error || blockingQueryError;

  const retryConversationQuery = async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: queryKeys.conversations.searchRoot() });
  };
  
  // Group conversations
  const { groups, orderedKeys } = useMemo(
    () => groupConversationsByDate(conversations),
    [conversations]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header with back button - matching User Settings style */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            height: '45px',
            px: { xs: 1, sm: 2 },
            py: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{ color: 'primary.main' }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              All Chat History ({total})
            </Typography>
          </Box>
          
          {selectedIds.size > 0 && (
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete ({selectedIds.size})
            </Button>
          )}
        </Box>

        {/* Main content */}
        <Container
          maxWidth="lg"
          sx={{ pt: 2, pb: 2, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
        {/* Filters */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3,
            bgcolor: (theme) => theme.palette.mode === 'light' ? '#f5f5f5' : '#333',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {/* Search */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' }, flexGrow: 1, minWidth: 0 }}>
              <TextField
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
                size="small"
                sx={{ 
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: BOSSARD_BLUE
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: BOSSARD_BLUE
                    }
                  }
                }}
                InputProps={{
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearchTerm(''); setCommittedSearch(''); }}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={triggerSearch}
                startIcon={<Search />}
                sx={{
                  bgcolor: BOSSARD_BLUE,
                  '&:hover': { bgcolor: '#005f99' },
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Search
              </Button>
            </Box>
            
            {/* Date Range */}
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <DatePicker
                label="From"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: { xs: '100%', sm: 150, md: 160 },
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper'
                      }
                    }
                  }
                }}
              />
            </Box>
            
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <DatePicker
                label="To"
                value={toDate}
                onChange={setToDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: { xs: '100%', sm: 150, md: 160 },
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper'
                      }
                    }
                  }
                }}
              />
            </Box>
            
            {/* Favorites Toggle */}
            <FormControlLabel
              sx={{ m: 0, width: { xs: '100%', sm: 'auto' } }}
              control={
                <Switch 
                  checked={favoritesOnly} 
                  onChange={(e) => setFavoritesOnly(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: BOSSARD_BLUE
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      bgcolor: BOSSARD_BLUE
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Favorite sx={{ fontSize: 18, color: favoritesOnly ? BOSSARD_BLUE : 'text.secondary' }} />
                  <Typography variant="body2">Favorites only</Typography>
                </Box>
              }
            />
            
            {/* Select All Toggle */}
            {conversations.length > 0 && (
              <FormControlLabel
                sx={{ m: 0, width: { xs: '100%', sm: 'auto' } }}
                control={
                  <Switch 
                    checked={selectedIds.size === conversations.length && conversations.length > 0}
                    onChange={handleSelectAll}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: BOSSARD_BLUE
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: BOSSARD_BLUE
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    Select all {selectedIds.size > 0 && `(${selectedIds.size})`}
                  </Typography>
                }
              />
            )}
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                size="small" 
                onClick={clearFilters}
                sx={{ color: 'text.secondary', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'center' } }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && conversations.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Stale data warning */}
        {showStaleDataWarning && (
          <Box sx={{ mb: 2 }}>
            <QueryStaleDataWarning
              message="Showing last synced data. We couldn't refresh chat history."
              onRetry={() => {
                void retryConversationQuery();
              }}
            />
          </Box>
        )}

        {/* Conversation List */}
        <Paper 
          elevation={0} 
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 2,
            overflow: 'auto',
            flex: 1,
            minHeight: 240
          }}
        >
          {loading && conversations.length === 0 ? (
            <QueryLoadingState label="Loading conversations..." minHeight={220} />
          ) : displayError && conversations.length === 0 ? (
            <QueryErrorState
              title="Unable to load chat history"
              message={displayError}
              onRetry={() => {
                void retryConversationQuery();
              }}
              minHeight={220}
            />
          ) : conversations.length === 0 ? (
            <QueryEmptyState
              title="No conversations found"
              description={
                hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Start a new chat to see your history here'
              }
              minHeight={220}
            />
          ) : (
            orderedKeys.map(groupKey => (
              <Box key={groupKey}>
                {/* Group Header */}
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontFamily: '"Dinot Black", Arial, sans-serif',
                    color: BOSSARD_BLUE,
                    fontWeight: 900,
                    px: 2,
                    py: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#fafafa' : '#2a2a2a',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {groupKey}
                </Typography>
                
                {/* Conversations in Group */}
                {groups[groupKey].map((conv, index) => (
                  <Box
                    key={conv.id}
                    onClick={() => handleConversationClick(conv)}
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      px: 2,
                      py: 0.75,
                      cursor: 'pointer',
                      borderBottom: index < groups[groupKey].length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: selectedIds.has(conv.id) ? BOSSARD_BLUE_10 : 'transparent',
                      '&:hover': {
                        bgcolor: BOSSARD_BLUE_10
                      },
                      transition: 'background-color 0.15s ease'
                    }}
                  >
                    {/* Checkbox */}
                    <Checkbox
                      checked={selectedIds.has(conv.id)}
                      onChange={(e) => handleSelectConversation(e, conv.id)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{
                        mr: 1,
                        p: 0.5,
                        '&.Mui-checked': { color: BOSSARD_BLUE }
                      }}
                    />
                    
                    {/* Content */}
                    <Box sx={{ flexGrow: 1, minWidth: 0, mr: { xs: 1, sm: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        {conv.agent_key === 'researcher' && (
                          <Typography
                            component="span"
                            aria-label="Researcher conversation"
                            sx={{
                              fontSize: '0.875rem',
                              mr: 0.75,
                              lineHeight: 1,
                              flexShrink: 0,
                            }}
                          >
                            🔬
                          </Typography>
                        )}
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {conv.title}
                        </Typography>
                      </Box>
                      {conv.preview && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 0.25,
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {conv.preview}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Favorite Button */}
                    <Tooltip title={conv.is_favourite ? 'Remove from favorites' : 'Add to favorites'}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleToggleFavorite(e, conv)}
                        sx={{ 
                          mr: 1,
                          p: 0.5,
                          color: conv.is_favourite ? BOSSARD_BLUE : 'text.secondary',
                          '&:hover': { bgcolor: BOSSARD_BLUE_10 }
                        }}
                      >
                        {conv.is_favourite ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    
                    {/* Date */}
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        whiteSpace: 'nowrap',
                        minWidth: { xs: 'auto', sm: 70 },
                        textAlign: 'right',
                        fontSize: '0.7rem',
                        mt: { xs: 0.5, sm: 0 },
                        ml: { xs: 0.5, sm: 0 },
                      }}
                    >
                      {formatDate(conv.updated_date)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))
          )}
          
          {/* Load More */}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                sx={{ color: BOSSARD_BLUE }}
              >
                {isFetchingNextPage ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Load more
              </Button>
            </Box>
          )}
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => !deleting && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              minHeight: 200,
              bgcolor: 'background.paper',
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 600, fontSize: '1.25rem' }}>
            Do you want to delete {selectedIds.size === 1 ? 'this chat' : 'these chats'}?
          </DialogTitle>
          
          <DialogContent sx={{ pb: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                This will permanently delete{' '}
                <Typography component="span" sx={{ fontWeight: 600 }}>
                  {selectedIds.size} conversation{selectedIds.size !== 1 ? 's' : ''}
                </Typography>
                {' '}and remove {selectedIds.size === 1 ? 'it' : 'them'} from your list of chats.
              </Typography>
              
              <Typography variant="body2" color="error" sx={{ fontWeight: 600 }}>
                All files in {selectedIds.size === 1 ? 'this chat' : 'these chats'} will be deleted.
              </Typography>
              
              {deleting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Deleting {selectedIds.size === 1 ? 'chat' : 'chats'}...
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              variant="outlined"
              color="primary"
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete} 
              variant="contained"
              color="primary"
              disabled={deleting}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default ChatHistoryPage;
